import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getMemberDetail, addDependent, getTezkar, addTezkar, deleteTezkar, getMyFollowupQrToken, updateMyContact, updateMyProfile, setMyWhatsappOptIn } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { FiUser, FiUsers, FiHeart, FiDollarSign, FiClock, FiPlus, FiTrash2, FiBookOpen, FiSmartphone, FiRefreshCw, FiEdit2 } from 'react-icons/fi';
import { QRCodeCanvas } from 'qrcode.react';
import DependentCard from '../../components/DependentCard';
import Toast from '../../components/Toast';
import PhoneInput from '../../components/PhoneInput';
import OutstandingBalance from '../../components/OutstandingBalance';

const currentYear = new Date().getFullYear();

const statusPill = { member: 'pill-green', inactive: 'pill-red', transferred: 'pill-blue', changed_religion: 'pill-gray', deceased: 'pill-gray', suspended: 'pill-red' };

const Field = ({ label, value }) => value ? (
  <div style={{ marginBottom: 14 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 14, color: '#111827', fontWeight: 500 }}>{value}</div>
  </div>
) : null;

const MyProfile = () => {
  const { t } = useTranslation();
  const statusLabel = {
    member: t('status.member'),
    inactive: t('status.inactive'),
    transferred: t('status.transferred'),
    changed_religion: t('status.changed_religion'),
    deceased: t('status.deceased'),
    suspended: t('status.suspended'),
  };
  const TABS = [
    { key: 'overview',       label: t('myProfile.overview'),     icon: <FiUser /> },
    { key: 'dependents',     label: t('members.dependents'),     icon: <FiUsers /> },
    { key: 'tezkar',         label: t('members.tezkar'),         icon: <FiBookOpen /> },
    { key: 'followups',      label: t('followup.title'),         icon: <FiHeart /> },
    { key: 'contributions',  label: t('myProfile.contributions'), icon: <FiDollarSign /> },
    { key: 'status_history', label: t('myProfile.statusHistory'), icon: <FiClock /> },
  ];
  const { user } = useAuth();
  const [member, setMember] = useState(null);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [addingDep, setAddingDep] = useState(false);
  const [newDep, setNewDep] = useState({ first_name: '', middle_name: '', last_name: '', relationship: 'wife', birth_year: '', holy_communion: 'unknown', country: 'Zambia', phone: '', whatsapp: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [tezkarList, setTezkarList] = useState([]);
  const [newTezkarName, setNewTezkarName] = useState('');
  const [addingTezkar, setAddingTezkar] = useState(false);
  const [qrToken, setQrToken] = useState(null);
  const [qrCountdown, setQrCountdown] = useState(0);
  const [showQr, setShowQr] = useState(false);
  const [qrSize, setQrSize] = useState(() => typeof window !== 'undefined' ? Math.min(260, window.innerWidth - 80) : 240);
  const [editingContact, setEditingContact] = useState(false);
  const [contactForm, setContactForm] = useState({ phone: '', whatsapp: '' });
  const [savingContact, setSavingContact] = useState(false);
  // v1.5.2 — full profile edit modal covering 11 whitelisted fields. The
  // small phone/whatsapp inline edit above stays for quick contact-only edits.
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState('');

  useEffect(() => {
    const handler = () => setQrSize(Math.min(260, window.innerWidth - 80));
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const refreshQr = () => {
    getMyFollowupQrToken().then(r => {
      setQrToken(r.data.token);
      setQrCountdown(r.data.expires_in || 300);
    }).catch(() => showToast(t('myProfile.qrLoadFailed'), 'error'));
  };

  useEffect(() => {
    if (!showQr) return;
    if (!qrToken) refreshQr();
    const tick = setInterval(() => {
      setQrCountdown(c => {
        if (c <= 1) { refreshQr(); return 300; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [showQr]);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = () => {
    if (!user?.member_id) return;
    getMemberDetail(user.member_id).then(r => setMember(r.data || null)).catch(() => {});
  };

  const startEditContact = () => {
    setContactForm({ phone: member?.phone || '', whatsapp: member?.whatsapp || '' });
    setEditingContact(true);
  };

  // v1.5.2 — full profile editor. Pre-fill from the current member row; on
  // save, only send what the user actually touched — backend also diff-checks
  // and only writes changed fields to the audit log.
  const startEditProfile = () => {
    setProfileError('');
    setProfileForm({
      middle_name:    member?.middle_name || '',
      last_name:      member?.last_name || '',
      christian_name: member?.christian_name || '',
      birth_year:     member?.birth_year || '',
      marital_status: member?.marital_status || '',
      occupation:     member?.occupation || '',
      phone:          member?.phone || '',
      whatsapp:       member?.whatsapp || '',
      email:          member?.email || '',
      holy_communion: member?.holy_communion || 'unknown',
      address:        member?.address || '',
    });
    setEditingProfile(true);
  };
  const saveProfile = async () => {
    setSavingProfile(true); setProfileError('');
    try {
      const payload = { ...profileForm };
      if (payload.birth_year === '' || payload.birth_year === null) delete payload.birth_year;
      const r = await updateMyProfile(payload);
      // Optimistic merge so the UI updates immediately without a re-fetch.
      setMember(m => ({ ...m, ...payload, birth_year: payload.birth_year ? parseInt(payload.birth_year, 10) : m?.birth_year }));
      setEditingProfile(false);
      const n = Array.isArray(r.data?.changed_fields) ? r.data.changed_fields.length : 0;
      showToast(n === 0 ? (t('common.saved') || 'Saved') : `${t('common.saved') || 'Saved'} — ${n} field${n === 1 ? '' : 's'} updated`);
      load();
    } catch (e) {
      setProfileError(e.response?.data?.error || (t('myProfile.failed') || 'Failed'));
    } finally { setSavingProfile(false); }
  };
  const saveContact = async () => {
    setSavingContact(true);
    try {
      await updateMyContact(contactForm);
      setMember(m => ({ ...m, phone: contactForm.phone, whatsapp: contactForm.whatsapp }));
      setEditingContact(false);
      showToast(t('common.saved') || 'Saved');
    } catch (e) { showToast(e.response?.data?.error || t('myProfile.failed'), 'error'); }
    finally { setSavingContact(false); }
  };
  const loadTezkar = () => {
    if (!user?.member_id) return;
    getTezkar(user.member_id).then(r => setTezkarList(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  };

  useEffect(() => {
    if (!user?.member_id) { setLoading(false); return; }
    getMemberDetail(user.member_id)
      .then(r => setMember(r.data || null))
      .catch(() => {})
      .finally(() => setLoading(false));
    loadTezkar();
  }, [user]);

  const handleAddDep = async () => {
    if (!newDep.first_name) return showToast(t('myProfile.firstNameRequired'), 'error');
    setSaving(true);
    try {
      await addDependent(user.member_id, { ...newDep, country: newDep.country || 'Zambia', phone: newDep.phone || null, whatsapp: newDep.whatsapp || null });
      setAddingDep(false);
      setNewDep({ first_name: '', middle_name: '', last_name: '', relationship: 'wife', birth_year: '', holy_communion: 'unknown', country: 'Zambia', phone: '', whatsapp: '' });
      showToast(t('myProfile.dependentAdded'));
      load();
    } catch (e) { showToast(t('myProfile.failed'), 'error'); }
    finally { setSaving(false); }
  };

  const setD = f => e => setNewDep(p => ({ ...p, [f]: e.target.value }));

  if (loading) return <div className="page-content" style={{ color: '#6b7280' }}>{t('common.loading')}</div>;

  if (!user?.member_id || !member) return (
    <div className="page-content">
      <div className="page-header"><div><h1>{t('nav.myProfile')}</h1></div></div>
      <div className="card">
        <div className="empty-state" style={{ padding: '60px 24px' }}>
          <div className="empty-icon"><FiUser /></div>
          <h3>{t('myProfile.profileNotLinked')}</h3>
          <p>{t('myProfile.profileNotLinkedDesc')}</p>
        </div>
      </div>
    </div>
  );

  const age = member.birth_year ? currentYear - parseInt(member.birth_year) : null;
  const fullName = [member.title, member.first_name, member.middle_name, member.last_name].filter(Boolean).join(' ');
  const totalContributions = (member.contributions || []).reduce((s, c) => s + parseFloat(c.amount), 0);
  // v1.9.53 — Split by currency so K payments don't render as "$300" and vice
  // versa. Same fix as MemberProfile.js (v1.9.46). Falls back to a raw bucket
  // for very old CRs that lack amount_usd / amount_kwacha.
  const totalsByCurrency = (member.contributions || []).reduce((acc, c) => {
    const usd = parseFloat(c.amount_usd || 0) || 0;
    const kwa = parseFloat(c.amount_kwacha || 0) || 0;
    if (usd > 0) acc['$'] = (acc['$'] || 0) + usd;
    if (kwa > 0) acc['K'] = (acc['K'] || 0) + kwa;
    if (!usd && !kwa) acc[''] = (acc[''] || 0) + parseFloat(c.amount || 0);
    return acc;
  }, {});
  const fmtMoney = (n) => parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const contribsStat = Object.keys(totalsByCurrency).length
    ? Object.entries(totalsByCurrency).map(([sym, amt]) => `${sym}${fmtMoney(amt)}`).join(' · ')
    : fmtMoney(totalContributions);

  return (
    <div className="page-content">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#4f46e5', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 700, flexShrink: 0 }}>
          {member.first_name?.[0]}{member.last_name?.[0]}
        </div>
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>{fullName}</h1>
          <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className={`pill ${statusPill[member.membership_status] || 'pill-gray'}`}>{statusLabel[member.membership_status] || member.membership_status}</span>
            {member.card_number && <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#4f46e5', fontWeight: 700, background: '#eef2ff', padding: '2px 8px', borderRadius: 6 }}>{member.card_number}</span>}
            {member.spiritual_father_name && <span style={{ fontSize: 12, color: '#6b7280' }}>{t('members.spiritualFather')}: <strong>{member.spiritual_father_name}</strong></span>}
          </div>
        </div>

        {/* Quick stats */}
        <div style={{ display: 'flex', gap: 12, flexShrink: 0, flexWrap: 'wrap' }}>
          {[
            { label: t('members.dependents'), value: member.dependents?.length || 0, color: '#7c3aed' },
            { label: t('followup.title'), value: member.followups?.length || 0, color: '#16a34a' },
            { label: t('myProfile.contributions'), value: contribsStat, color: '#2563eb' },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: 'center', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 14px', minWidth: 80, flex: '1 1 80px' }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* v1.6.0 — outstanding-debt banner (compact) at the top so the member
          sees it every time they open the portal until settled. */}
      {member?.id && <OutstandingBalance memberId={member.id} readOnly compact />}

      {/* QR for spiritual-father visit */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{t('myProfile.qrTitle')}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{t('myProfile.qrDesc')}</div>
          </div>
          <button className="btn btn-primary" onClick={() => setShowQr(true)}><FiSmartphone /> {t('myProfile.showQr')}</button>
        </div>
      </div>

      {/* v1.5.2 — full profile edit modal (11 whitelisted fields). Identity
          fields (first_name, gender, card_number, membership_status,
          spiritual father, family links) intentionally omitted — backend
          also drops anything outside the whitelist. */}
      {editingProfile && (
        <div className="modal-overlay" onClick={() => !savingProfile && setEditingProfile(false)} style={{ alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal" style={{ maxWidth: 560, width: '94%' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>{t('myProfile.editMyDetails') || 'Edit my details'}</h3>
                <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>{t('myProfile.editHint') || 'You can update the fields below. First name, gender, and church-side settings can only be changed by an admin.'}</p>
              </div>
              <button className="btn-icon" onClick={() => !savingProfile && setEditingProfile(false)} disabled={savingProfile}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {profileError && <div className="error-msg" style={{ marginBottom: 12 }}>{profileError}</div>}
              <div className="form-grid">
                <div className="form-group">
                  <label>{t('members.middleName') || 'Middle Name'}</label>
                  <input value={profileForm.middle_name || ''} onChange={e => setProfileForm(f => ({ ...f, middle_name: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>{t('members.lastName') || 'Last Name'}</label>
                  <input value={profileForm.last_name || ''} onChange={e => setProfileForm(f => ({ ...f, last_name: e.target.value }))} />
                </div>
                <div className="form-group form-full">
                  <label>{t('members.christianName') || 'Christian Name'}</label>
                  <input value={profileForm.christian_name || ''} onChange={e => setProfileForm(f => ({ ...f, christian_name: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>{t('members.yearOfBirth') || 'Year of Birth'}</label>
                  <input type="number" value={profileForm.birth_year || ''} min="1900" max={new Date().getFullYear()}
                    onChange={e => setProfileForm(f => ({ ...f, birth_year: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>{t('members.maritalStatus') || 'Marital Status'}</label>
                  <select value={profileForm.marital_status || ''} onChange={e => setProfileForm(f => ({ ...f, marital_status: e.target.value }))}>
                    <option value="">—</option>
                    <option value="single">{t('members.single') || 'Single'}</option>
                    <option value="married">{t('members.married') || 'Married'}</option>
                    <option value="divorced">{t('members.divorced') || 'Divorced'}</option>
                    <option value="widowed">{t('members.widowed') || 'Widowed'}</option>
                  </select>
                </div>
                <div className="form-group form-full">
                  <label>{t('members.occupation') || 'Occupation'}</label>
                  <input value={profileForm.occupation || ''} onChange={e => setProfileForm(f => ({ ...f, occupation: e.target.value }))} />
                </div>
                <div className="form-group form-full">
                  <label>{t('common.phone') || 'Phone'}</label>
                  <PhoneInput value={profileForm.phone} onChange={v => setProfileForm(f => ({ ...f, phone: v }))} />
                </div>
                <div className="form-group form-full">
                  <label>WhatsApp</label>
                  <PhoneInput value={profileForm.whatsapp} onChange={v => setProfileForm(f => ({ ...f, whatsapp: v }))} />
                </div>
                <div className="form-group form-full">
                  <label>{t('common.email') || 'Email'}</label>
                  <input type="email" value={profileForm.email || ''} onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))} placeholder="you@example.com" />
                </div>
                <div className="form-group form-full">
                  <label>{t('members.holyCommunion') || 'Holy Communion'}</label>
                  <select value={profileForm.holy_communion || 'unknown'} onChange={e => setProfileForm(f => ({ ...f, holy_communion: e.target.value }))}>
                    <option value="unknown">— {t('common.select') || 'Select'} —</option>
                    <option value="taking">{t('members.taking') || 'Taking Communion'}</option>
                    <option value="not_taking">{t('members.notTaking') || 'Not Taking'}</option>
                    <option value="excommunicated">{t('members.excommunicated') || 'Excommunicated'}</option>
                  </select>
                </div>
                <div className="form-group form-full">
                  <label>{t('common.address') || 'Address'}</label>
                  <textarea rows={2} value={profileForm.address || ''} onChange={e => setProfileForm(f => ({ ...f, address: e.target.value }))} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditingProfile(false)} disabled={savingProfile}>{t('common.cancel') || 'Cancel'}</button>
              <button className="btn btn-primary" onClick={saveProfile} disabled={savingProfile}>{savingProfile ? (t('common.saving') || 'Saving…') : (t('common.save') || 'Save')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Large QR Modal */}
      {showQr && (
        <div className="modal-overlay" onClick={() => setShowQr(false)} style={{ alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal" style={{ maxWidth: 420, width: '92%' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>{t('myProfile.qrShowToFather')}</h3>
                <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>{t('myProfile.qrRefreshesIn')} <strong>{Math.floor(qrCountdown/60)}:{String(qrCountdown%60).padStart(2,'0')}</strong></p>
              </div>
              <button className="btn-icon" onClick={() => setShowQr(false)}><FiSmartphone style={{ visibility: 'hidden' }} />✕</button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center', padding: 24 }}>
              <div style={{ display: 'inline-block', background: '#fff', padding: 12, border: '2px solid #e5e7eb', borderRadius: 12 }}>
                {qrToken
                  ? <QRCodeCanvas value={qrToken} size={qrSize} />
                  : <div style={{ width: qrSize, height: qrSize, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>{t('common.loading')}</div>}
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
                <button className="btn btn-secondary" onClick={refreshQr}><FiRefreshCw /> {t('myProfile.refresh')}</button>
                <button className="btn btn-primary" onClick={() => setShowQr(false)}>{t('myProfile.hide')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', marginBottom: 20, overflowX: 'auto' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px',
            border: 'none', background: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
            fontWeight: 600, fontSize: 13,
            color: tab === t.key ? '#4f46e5' : '#6b7280',
            borderBottom: tab === t.key ? '2px solid #4f46e5' : '2px solid transparent', marginBottom: -2,
          }}>
            {t.icon} {t.label}
            {t.key === 'dependents' && member.dependents?.length > 0 && <span style={{ background: '#ede9fe', color: '#7c3aed', borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>{member.dependents.length}</span>}
            {t.key === 'tezkar' && tezkarList.length > 0 && <span style={{ background: '#fef3c7', color: '#b45309', borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>{tezkarList.length}</span>}
            {t.key === 'contributions' && member.contributions?.length > 0 && <span style={{ background: '#dbeafe', color: '#2563eb', borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>{member.contributions.length}</span>}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
          <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{t('members.personalInfo')}</span>
              <button className="btn btn-sm btn-secondary" onClick={startEditProfile}>
                <FiEdit2 /> {t('myProfile.editMyDetails') || 'Edit my details'}
              </button>
            </div>
            <div className="card-body">
              <Field label={t('members.fullName')} value={fullName} />
              <Field label={t('members.christianName')} value={member.christian_name} />
              <Field label={t('members.gender')} value={member.gender ? member.gender.charAt(0).toUpperCase() + member.gender.slice(1) : null} />
              <Field label={t('members.yearOfBirth')} value={member.birth_year ? `${member.birth_year} (${t('members.age')} ${age})` : null} />
              <Field label={t('members.maritalStatus')} value={member.marital_status} />
              <Field label={t('members.occupation')} value={member.occupation} />
              <Field label={t('common.address')} value={member.address} />
              <Field label={t('common.email') || 'Email'} value={member.email} />
            </div>
          </div>

          <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{t('common.phone')}</span>
              {!editingContact && <button className="btn btn-sm btn-secondary" onClick={startEditContact}><FiEdit2 /> {t('common.edit') || 'Edit'}</button>}
            </div>
            <div className="card-body">
              {editingContact ? (
                <>
                  <div className="form-group form-full">
                    <label>{t('common.phone')}</label>
                    <PhoneInput value={contactForm.phone} onChange={v => setContactForm(f => ({ ...f, phone: v }))} />
                  </div>
                  <div className="form-group form-full" style={{ marginTop: 10 }}>
                    <label>WhatsApp</label>
                    <PhoneInput value={contactForm.whatsapp} onChange={v => setContactForm(f => ({ ...f, whatsapp: v }))} />
                  </div>
                  <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                    <button className="btn btn-sm btn-primary" onClick={saveContact} disabled={savingContact}>{savingContact ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}</button>
                    <button className="btn btn-sm btn-secondary" onClick={() => setEditingContact(false)} disabled={savingContact}>{t('common.cancel') || 'Cancel'}</button>
                  </div>
                </>
              ) : (
                <>
                  <Field label={t('common.phone')} value={member.phone} />
                  <Field label="WhatsApp" value={member.whatsapp} />
                  {!member.phone && !member.whatsapp && <p style={{ color: '#9ca3af', fontSize: 13 }}>No contact numbers yet — tap Edit to add.</p>}
                  {(member.phone || member.whatsapp) && (
                    <div style={{ marginTop: 12, padding: '10px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#166534', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 14 }}>💬</span> WhatsApp reminders
                        </div>
                        <div style={{ fontSize: 11, color: '#4b5563', marginTop: 2 }}>
                          One weekly message if you have overdue contributions.
                        </div>
                      </div>
                      <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', gap: 6 }}>
                        <input
                          type="checkbox"
                          checked={!!member.whatsapp_opt_in}
                          onChange={async e => {
                            const next = e.target.checked ? 1 : 0;
                            setMember(m => ({ ...m, whatsapp_opt_in: next }));
                            try { await setMyWhatsappOptIn(next); }
                            catch { setMember(m => ({ ...m, whatsapp_opt_in: next ? 0 : 1 })); }
                          }}
                          style={{ width: 18, height: 18, cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: 12, fontWeight: 600, color: member.whatsapp_opt_in ? '#166534' : '#6b7280' }}>
                          {member.whatsapp_opt_in ? 'On' : 'Off'}
                        </span>
                      </label>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">{t('members.churchInfo')}</div>
            <div className="card-body">
              <Field label={t('members.membershipCard')} value={member.card_number} />
              <Field label={t('common.status')} value={statusLabel[member.membership_status] || member.membership_status} />
              <Field label={t('members.spiritualFather')} value={member.spiritual_father_name} />
              <Field label={t('members.holyCommunion')} value={member.holy_communion ? member.holy_communion.replace('_', ' ') : null} />
              <Field label={t('myProfile.memberSince')} value={member.created_at?.slice(0, 10)} />
            </div>
          </div>
        </div>
      )}

      {/* DEPENDENTS */}
      {tab === 'dependents' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <strong style={{ fontSize: 14 }}>{t('myProfile.dependentsRegistered', { count: member.dependents?.length || 0 })}</strong>
            <button className="btn btn-secondary" onClick={() => setAddingDep(!addingDep)}><FiPlus /> {t('members.addDependent')}</button>
          </div>

          {addingDep && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header">{t('myProfile.newDependent')}</div>
              <div className="card-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>{t('members.relationship')}</label>
                    <select value={newDep.relationship} onChange={e => {
                      const rel = e.target.value;
                      setNewDep(p => ({ ...p, relationship: rel,
                        middle_name: (rel === 'son' || rel === 'daughter') ? member.first_name : '',
                        last_name: (rel === 'son' || rel === 'daughter') ? (member.middle_name || '') : ''
                      }));
                    }}>
                      {member.gender !== 'female' && <option value="wife">{t('members.wife')}</option>}
                      {member.gender !== 'male' && <option value="husband">{t('members.husband')}</option>}
                      <option value="son">{t('members.son')}</option>
                      <option value="daughter">{t('members.daughter')}</option>
                      <option value="other">{t('members.other')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('members.yearOfBirth')} {newDep.birth_year && <span style={{ color: '#4f46e5', fontWeight: 400 }}>→ {t('members.age')} {currentYear - parseInt(newDep.birth_year)}</span>}</label>
                    <input type="number" value={newDep.birth_year} onChange={setD('birth_year')} min="1900" max={currentYear} />
                  </div>
                  <div className="form-group"><label>{t('members.firstName')} <span className="required">*</span></label><input value={newDep.first_name} onChange={setD('first_name')} /></div>
                  <div className="form-group"><label>{t('members.middleName')}</label><input value={newDep.middle_name} onChange={setD('middle_name')} /></div>
                  <div className="form-group"><label>{t('members.lastName')}</label><input value={newDep.last_name} onChange={setD('last_name')} /></div>
                  <div className="form-group">
                    <label>{t('members.holyCommunion')}</label>
                    <select value={newDep.holy_communion} onChange={setD('holy_communion')}>
                      <option value="unknown">{t('myProfile.selectPlaceholder')}</option>
                      <option value="taking">{t('members.taking')}</option>
                      <option value="not_taking">{t('members.notTaking')}</option>
                      <option value="excommunicated">{t('members.excommunicated')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('members.whereTheyLive')}</label>
                    <select
                      value={newDep.country === 'Zambia' || !newDep.country ? 'Zambia' : 'Other'}
                      onChange={(e) => {
                        const v = e.target.value;
                        setNewDep(p => ({ ...p, country: v === 'Zambia' ? 'Zambia' : '' }));
                      }}
                    >
                      <option value="Zambia">{t('members.liveZambia')}</option>
                      <option value="Other">{t('members.liveOther')}</option>
                    </select>
                    {newDep.country !== 'Zambia' && (
                      <input
                        style={{ marginTop: 6 }}
                        value={newDep.country || ''}
                        onChange={setD('country')}
                        placeholder={t('members.countryNamePlaceholder')}
                      />
                    )}
                  </div>
                  {/* v1.5.1 — phone/whatsapp on the sub-member. */}
                  <div className="form-group">
                    <label>{t('common.phone')}</label>
                    <PhoneInput value={newDep.phone} onChange={(v) => setNewDep(p => ({ ...p, phone: v }))} />
                  </div>
                  <div className="form-group">
                    <label>WhatsApp</label>
                    <PhoneInput value={newDep.whatsapp} onChange={(v) => setNewDep(p => ({ ...p, whatsapp: v }))} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button className="btn btn-primary" onClick={handleAddDep} disabled={saving}>{saving ? t('common.saving') : t('common.save')}</button>
                  <button className="btn btn-secondary" onClick={() => setAddingDep(false)}>{t('common.cancel')}</button>
                </div>
              </div>
            </div>
          )}

          {(member.subMembers || []).length > 0 && (
            <div className="card" style={{ marginBottom: 16, border: '1px solid #c7d2fe', background: '#eef2ff' }}>
              <div className="card-header" style={{ color: '#3730a3' }}>
                Family members with their own profile ({member.subMembers.length})
              </div>
              <div className="card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {member.subMembers.map(s => (
                  <div key={s.id} style={{ background: '#fff', border: '1px solid #c7d2fe', borderRadius: 8, padding: '8px 12px', fontSize: 13, minWidth: 180 }}>
                    <div style={{ fontWeight: 700, color: '#111827' }}>{[s.first_name, s.middle_name, s.last_name].filter(Boolean).join(' ')}</div>
                    <div style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace', marginTop: 2 }}>{s.card_number}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(member.dependents || []).length === 0 ? (
            <div className="empty-state"><div className="empty-icon"><FiUsers /></div><p>{t('members.noDependents')}</p></div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              {member.dependents.map(d => (
                <div key={d.id} style={{ position: 'relative' }}>
                  <DependentCard d={d} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TEZKAR */}
      {tab === 'tezkar' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <strong style={{ fontSize: 14 }}>{t('myProfile.tezkarRegistered', { count: tezkarList.length })}</strong>
            <button className="btn btn-secondary" onClick={() => setAddingTezkar(!addingTezkar)}><FiPlus /> {t('members.addName')}</button>
          </div>

          {addingTezkar && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-body">
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    value={newTezkarName}
                    onChange={e => setNewTezkarName(e.target.value)}
                    placeholder={t('myProfile.deceasedFullName')}
                    style={{ flex: 1, minWidth: 200 }}
                    autoFocus
                    onKeyDown={async e => {
                      if (e.key === 'Enter' && newTezkarName.trim()) {
                        try {
                          await addTezkar(user.member_id, { name: newTezkarName.trim() });
                          setNewTezkarName('');
                          setAddingTezkar(false);
                          showToast(t('myProfile.nameAdded'));
                          loadTezkar();
                        } catch { showToast(t('myProfile.failed'), 'error'); }
                      }
                    }}
                  />
                  <button className="btn btn-primary" onClick={async () => {
                    if (!newTezkarName.trim()) return;
                    try {
                      await addTezkar(user.member_id, { name: newTezkarName.trim() });
                      setNewTezkarName('');
                      setAddingTezkar(false);
                      showToast(t('myProfile.nameAdded'));
                      loadTezkar();
                    } catch { showToast(t('myProfile.failed'), 'error'); }
                  }}>{t('common.save')}</button>
                  <button className="btn btn-secondary" onClick={() => { setAddingTezkar(false); setNewTezkarName(''); }}>{t('common.cancel')}</button>
                </div>
              </div>
            </div>
          )}

          {tezkarList.length === 0 ? (
            <div className="empty-state"><div className="empty-icon"><FiBookOpen /></div><p>{t('members.noTezkar')}</p></div>
          ) : (
            <div className="card">
              <div className="card-body" style={{ padding: 0 }}>
                {tezkarList.map((tz, i) => (
                  <div key={tz.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderBottom: i < tezkarList.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{tz.name}</span>
                    <button className="btn-icon danger" onClick={async () => {
                      if (!window.confirm(t('myProfile.removeFromMemorial', { name: tz.name }))) return;
                      try {
                        await deleteTezkar(tz.id);
                        showToast(t('myProfile.removed'), 'error');
                        loadTezkar();
                      } catch { showToast(t('myProfile.failed'), 'error'); }
                    }}><FiTrash2 /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* FOLLOW-UPS */}
      {tab === 'followups' && (
        <div>
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>{t('myProfile.followupVisitsRecorded', { count: member.followups?.length || 0 })}</p>
          {(member.followups || []).length === 0 ? (
            <div className="empty-state"><div className="empty-icon"><FiHeart /></div><p>{t('followup.noFollowups')}</p></div>
          ) : (
            <div className="table-card">
              <table>
                <thead><tr><th>#</th><th>{t('common.date')}</th><th>{t('common.status')}</th><th>{t('followup.nextFollowup')}</th><th>{t('common.notes')}</th><th>{t('table.by')}</th></tr></thead>
                <tbody>
                  {member.followups.map((f, i) => (
                    <tr key={f.id}>
                      <td data-label="#">{i + 1}</td>
                      <td data-label={t('common.date')}>{f.date}</td>
                      <td data-label={t('common.status')}>{f.status && <span className={`pill ${f.status === 'good' ? 'pill-green' : f.status === 'critical' ? 'pill-red' : f.status === 'needs_attention' ? 'pill-yellow' : 'pill-gray'}`}>{f.status.replace('_', ' ')}</span>}</td>
                      <td data-label={t('followup.nextFollowup')}>{f.next_followup_date || '—'}</td>
                      <td data-label={t('common.notes')} style={{ fontStyle: 'italic', color: '#374151' }}>{f.notes || '—'}</td>
                      <td data-label={t('table.by')}>{f.done_by_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* CONTRIBUTIONS */}
      {tab === 'contributions' && (
        <div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            <div className="card" style={{ flex: 1, padding: '14px 20px' }}>
              <div style={{ fontSize: 12, color: '#6b7280' }}>{t('myProfile.totalContributions')}</div>
              {/* v1.9.53 — split per currency so K-only payers don't see a $
                  prefix on their K total. */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {Object.entries(totalsByCurrency).map(([sym, amt]) => (
                  <div key={sym || 'raw'} style={{ fontSize: 20, fontWeight: 800, color: '#2563eb' }}>
                    {sym}{fmtMoney(amt)}
                  </div>
                ))}
                {Object.keys(totalsByCurrency).length === 0 && (
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#9ca3af' }}>—</div>
                )}
              </div>
            </div>
            <div className="card" style={{ flex: 1, padding: '14px 20px' }}>
              <div style={{ fontSize: 12, color: '#6b7280' }}>{t('myProfile.transactions')}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#7c3aed' }}>{member.contributions?.length || 0}</div>
            </div>
          </div>
          {(member.contributions || []).length === 0 ? (
            <div className="empty-state"><div className="empty-icon"><FiDollarSign /></div><p>{t('myProfile.noContributions')}</p></div>
          ) : (
            <div className="table-card">
              <table>
                <thead><tr><th>#</th><th>{t('common.date')}</th><th>{t('finance.crNumber')}</th><th>{t('table.type')}</th><th>{t('myProfile.method')}</th><th>{t('finance.amountUSD')}</th><th>{t('finance.amountKwacha')}</th></tr></thead>
                <tbody>
                  {member.contributions.map((c, i) => (
                    <tr key={c.id}>
                      <td data-label="#">{i + 1}</td>
                      <td data-label={t('common.date')}>{c.date}</td>
                      <td data-label={t('finance.crNumber')}><span className="pill pill-gray" style={{ fontFamily: 'monospace', fontSize: 11 }}>{c.cr_number}</span></td>
                      <td data-label={t('table.type')}>{c.fund_name || '—'}</td>
                      <td data-label={t('myProfile.method')} style={{ textTransform: 'capitalize' }}>{c.payment_method}</td>
                      <td data-label={t('finance.amountUSD')} style={{ color: '#16a34a', fontWeight: 600 }}>{parseFloat(c.amount_usd || 0) > 0 ? `$${parseFloat(c.amount_usd).toFixed(2)}` : '—'}</td>
                      <td data-label={t('finance.amountKwacha')} style={{ color: '#2563eb', fontWeight: 600 }}>{parseFloat(c.amount_kwacha || 0) > 0 ? `K${parseFloat(c.amount_kwacha).toFixed(2)}` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />

      {/* STATUS HISTORY */}
      {tab === 'status_history' && (
        <div>
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>{t('myProfile.eventsRecorded', { count: (member.statusHistory?.length || 0) + 1 })}</p>

          <div style={{ display: 'flex', gap: 16, marginBottom: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20 }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#4f46e5' }} />
              {member.statusHistory?.length > 0 && <div style={{ width: 2, flex: 1, background: '#e5e7eb', marginTop: 4 }} />}
            </div>
            <div style={{ paddingBottom: 20, flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{t('myProfile.memberRegistered')}</div>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{member.created_at?.slice(0, 16)}</div>
              <div style={{ fontSize: 12, color: '#374151', marginTop: 4 }}>{t('myProfile.card')}: <strong>{member.card_number || '—'}</strong></div>
            </div>
          </div>

          {(member.statusHistory || []).length === 0 ? (
            <div style={{ color: '#9ca3af', fontSize: 13, marginLeft: 36 }}>{t('myProfile.noStatusChanges')}</div>
          ) : [...member.statusHistory].reverse().map((h, i, arr) => (
            <div key={h.id} style={{ display: 'flex', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#7c3aed', flexShrink: 0 }} />
                {i < arr.length - 1 && <div style={{ width: 2, flex: 1, background: '#e5e7eb', marginTop: 4 }} />}
              </div>
              <div style={{ paddingBottom: 24, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                  {h.old_status && <span className={`pill ${statusPill[h.old_status] || 'pill-gray'}`} style={{ fontSize: 12 }}>{statusLabel[h.old_status] || h.old_status}</span>}
                  {h.old_status && <span style={{ color: '#9ca3af', fontWeight: 700 }}>→</span>}
                  <span className={`pill ${statusPill[h.new_status] || 'pill-gray'}`} style={{ fontSize: 12 }}>{statusLabel[h.new_status] || h.new_status}</span>
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>{h.changed_at?.slice(0, 16)}</div>
                {(h.reason || h.custom_reason) && <div style={{ fontSize: 13, color: '#374151' }}>{t('myProfile.reasonLabel')}: {h.custom_reason || h.reason}</div>}
                {h.notes && <div style={{ fontSize: 13, color: '#374151', marginTop: 2, fontStyle: 'italic' }}>{h.notes}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyProfile;
